import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { OrdenPrestador } from 'src/app/models/OrdenPrestador';
import { EstadoOrden } from 'src/app/models/EstadoOrden';
import { API_URI } from 'src/environments/environment';

@Component({
    selector: 'app-more-orden-prestador-detalle',
    templateUrl: './more-orden-prestador-detalle.component.html',
    styleUrls: ['./more-orden-prestador-detalle.component.css'],
    standalone: false
})
export class MoreOrdenPrestadorDetalleComponent implements OnInit {
  private _authService = inject(AuthService);
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  private _aRoute = inject(ActivatedRoute);

  apiUri = API_URI;
  id = Number(this._aRoute.snapshot.paramMap.get('id'));
  orden = signal<OrdenPrestador | null>(null);
  estadosOrdenes = signal<EstadoOrden[]>([]);
  // Estado elegido en el combo, todavía no guardado — cambiar la orden es
  // un paso de dos tiempos: elegir y después apretar "Guardar".
  estadoSeleccionado = signal<number | null>(null);
  error = signal<string>('');
  message = signal<string>('');
  guardando = signal<boolean>(false);

  get hayCambiosSinGuardar(): boolean {
    return this.estadoSeleccionado() !== null && this.estadoSeleccionado() !== this.orden()?.id_estado_orden;
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    if (!this.id) {
      this.router.navigate(['/more-ordenes-prestador']);
      return;
    }

    try {
      await this._loadingService.run(async () => {
        await this._authService.ensureSession();
        this.estadosOrdenes.set(await firstValueFrom(this._orderPrestadorService.getEstadosOrdenes()));

        const res = await firstValueFrom(this._orderPrestadorService.getOrdenPrestador(this.id));
        if (!res) {
          this.error.set('No se encontró la orden.');
          return;
        }
        this.orden.set(res);
        this.estadoSeleccionado.set(res.id_estado_orden ?? null);
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar la orden.');
    }
  }

  onEstadoSeleccionadoChange(value: string): void {
    this.estadoSeleccionado.set(Number(value));
  }

  async guardarEstado(): Promise<void> {
    const actual = this.orden();
    const id_estado_orden = this.estadoSeleccionado();
    if (!actual || !id_estado_orden || id_estado_orden === actual.id_estado_orden) return;

    this.guardando.set(true);
    this.error.set('');

    try {
      await firstValueFrom(this._orderPrestadorService.updateEstadoOrdenPrestador(this.id, id_estado_orden));
      this.orden.set({
        ...actual,
        id_estado_orden,
        estadoOrden: this.estadosOrdenes().find(e => e.id === id_estado_orden)?.x_descripcion ?? actual.estadoOrden,
      });
      this.message.set('Estado actualizado correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      console.error(e);
      // revertir el combo al estado real, ya que no se pudo guardar
      this.estadoSeleccionado.set(actual.id_estado_orden ?? null);
      this.error.set('No se pudo actualizar el estado.');
    } finally {
      this.guardando.set(false);
    }
  }

  openReceta(): void {
    const x_img_receta = this.orden()?.x_img_receta;
    if (!x_img_receta) return;
    window.open(`${this.apiUri}uploads/images/${x_img_receta}`, '_blank');
  }

  // Mismo criterio que la pantalla de detalle del cliente (order-detail-prestador):
  // no hay un campo de "fecha de pago" propio, el estado de pago se infiere del
  // estado de la orden (lo administra Mercado Pago del lado del prestador).
  get textoPago(): string {
    switch (this.orden()?.id_estado_orden ?? 0) {
      case 3: case 4: case 6: case 9:
        return 'Abonado con Mercado Pago';
      case 7:
        return 'Pago en proceso';
      case 8:
        return 'Pago rechazado';
      case 11:
        return 'Pago cancelado';
      default:
        return 'Pago pendiente';
    }
  }

  back(): void {
    this._location.back();
  }
}
